import { useContext, useEffect, useState, memo } from 'react';
import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import useAlert from '../../../Alerts/useAlert';
import { usePrivileges } from '../../../Auth/usePrivileges';
import { LotDetailContext } from '../../../Contexts/LotDetailContext';
import { LotStatus, Warning } from '../../../Types/Lot';
import { useTranslationUtils } from '../../../Utilities/TranslationUtils';
import { useHttpUtils } from '../../../Utilities/useHttpUtils';

interface WarningsSubSectionProps {
    warnings: Warning[],
    category: string,
    notCategory?: string,
    sectionTitle?: string,
}

function WarningsCategorySection({ warnings, category, notCategory, sectionTitle }: WarningsSubSectionProps) {
    const { ts } = useTranslationUtils();

    if (!warnings) {
        return <></>;
    }

    var categoryWarnings = warnings;
    if (category === "*") {
        categoryWarnings = warnings.filter((warning) => !notCategory?.includes(warning.category));
    } else {
        categoryWarnings = warnings.filter((warning) => warning.category === category);
    }

    if (categoryWarnings.length === 0) {
        return <></>;
    }

    var warningList = categoryWarnings.map((warning, i) => (
        <li style={{ whiteSpace: "pre-wrap" }} key={`${category}-warning-${i}`}>{ts(warning.message)}</li>
    ));

    return (
        <>
            <b>{sectionTitle}</b>
            <ul className="warnings">
                {warningList}
            </ul>
        </>
    );
}

function WarningsBlockedSection({ warnings, category }: WarningsSubSectionProps) {
    const { t } = useTranslation();
    const { ts } = useTranslationUtils();
    const { lotDetail: { summary: { lotNumber, currentStatus }}, getLotDetail } = useContext(LotDetailContext);
    const { setAlert } = useAlert();
    const { fetchDataAuthenticated } = useHttpUtils();
    const { canReview } = usePrivileges();
    const [canBlock, setCanBlock] = useState(!!canReview && currentStatus !== LotStatus.Graded);
    useEffect(() => {
        setCanBlock(!!canReview && currentStatus !== LotStatus.Graded);
    }, [canReview, currentStatus]);

    if (!warnings) {
        return <></>;
    }

    const unblockLot = () => {
        fetchDataAuthenticated(`/api/v1/lot/${lotNumber}/unblock`, 'PUT')
            .then(async response => {
                if (response.ok) {
                    getLotDetail();
                } else {
                    throw new Error("Failed to unblock the lot");
                }
            })
            .catch(error => {
                setAlert("danger", "Error", error.message);
            });
    };

    var categoryWarnings = warnings.filter((warning) => warning.category === category);
    if (categoryWarnings.length === 0) {
        return <></>;
    }

    return (
        <>
            <b>{t('LotDetails.Warnings.WarningsFromBlocked')}</b>
            <ul>
            {categoryWarnings.map((warning, i) =>
                <li key={`block-warning-${i}`}>
                    {ts(warning.message)}
                    {warning.category === "BLOCKED" &&
                        <Button variant="primary ms-2 me-1"
                            disabled={!canBlock}
                            onClick={() => unblockLot()}>
                            {t('LotDetails.Warnings.Unblock')}
                        </Button>}
                </li>
            )}
            </ul>
        </>
    );
}

const WarningsSection = memo(function WarningsSection() {
    const { t } = useTranslation();
    const { lotDetail: { warnings } } = useContext(LotDetailContext);

    if (!warnings) {
        return <></>;
    }

    return (
        <>
            <WarningsCategorySection warnings={warnings} category="FAILURE" sectionTitle={t('LotDetails.Warnings.WarningsFromFailures')} />
            <WarningsBlockedSection warnings={warnings} category="BLOCKED" />
            <WarningsCategorySection warnings={warnings} category="PROCESS" sectionTitle={t('LotDetails.Warnings.WarningsFromProcessing')} />
            <WarningsCategorySection warnings={warnings} category="REVIEW" sectionTitle={t('LotDetails.Warnings.WarningsFromLotReview')} />
            <WarningsCategorySection warnings={warnings} category="GRADE" sectionTitle={t('LotDetails.Warnings.WarningsFromGrading')} />
            <WarningsCategorySection warnings={warnings} category="*" notCategory="FAILURE,BLOCKED,PROCESS,REVIEW,GRADE" sectionTitle={t('LotDetails.Warnings.OtherWarnings')} />
        </>
    );
});

export default WarningsSection;